<template>
  <div class="product-page-header">
    <SBreadcrumbsSeo
      :breadcrumbs="[
        { label: 'Home', href: ziggy('mainSite.homepage') },
        ...parentBreadcrumbs,
        { label: title },
      ]"
      class="product-page-header__breadcrumbs"
    />
    <div class="product-page-header__content">
      <div class="product-page-header__text">
        <SHeading level="1" size="1">{{ title }}</SHeading>
        <ContentRichText
          v-if="description"
          class="product-page-header__description"
          :content="description"
        />
      </div>
      <img
        v-if="headerImage"
        :alt="title"
        class="product-page-header__image"
        :src="headerImage.url"
      />
      <ContentCtaForm
        v-if="formUrl"
        card
        class="product-page-header__form"
        :form-submit-url="formUrl"
        :geo-product-slug="geoProductSlug ?? undefined"
        :show-usps="showUsps"
        :submit-label="submitLabel"
        :title="searchTitle ?? undefined"
      >
        <template v-if="showCompanies && geoProductSlug" #bottom>
          <HeaderCompanies :geo-product-slug="geoProductSlug" />
        </template>
      </ContentCtaForm>
      <ContentPageMeta
        :author="author"
        class="product-page-header__meta"
        :last-update="lastUpdate"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AssetValue } from "@solvari/statamic";

import { SHeading } from "@solvari/components";

import { ziggy } from "@/shared/libs/ziggy";
import HeaderCompanies from "~/features/product/components/HeaderCompanies.vue";
import ContentCtaForm from "~/shared/components/content-blocks/ContentCtaForm.vue";
import ContentRichText from "~/shared/components/content-blocks/ContentRichText.vue";
import ContentPageMeta from "~/shared/components/content-blocks/internal/ContentPageMeta.vue";
import SBreadcrumbsSeo from "~/shared/components/SBreadcrumbsSeo.vue";

const props = withDefaults(
  defineProps<{
    author?: string | null;
    description: string | null;
    formUrl: string | null | undefined;
    geoProductSlug?: string | null;
    headerImage: AssetValue | null;
    lastUpdate: string;
    parentBreadcrumbs?: { href: string; label: string }[];
    searchTitle: string | null;
    showCompanies?: boolean;
    showUsps?: boolean;
    submitLabel?: string | null;
    title: string;
  }>(),
  {
    author: null,
    parentBreadcrumbs: () => [],
  },
);
</script>

<style lang="postcss">
.product-page-header {
  container-type: inline-size;

  &__content {
    @apply mt-8 grid pb-6 gap-x-8;
    grid-template:
      "text"
      "." theme("spacing.8")
      "form"
      "." theme("spacing.3")
      "meta";
  }

  &__text {
    @apply flex flex-col area-[text];
  }

  &__description {
    @apply text-base;
  }

  &__image {
    @apply hidden aspect-square rounded-xl object-cover area-[image];
  }

  &__form {
    @apply area-[form];
  }

  &__meta {
    @apply justify-self-end area-[meta];
  }

  @container (min-width: 600px) {
    &__content {
      @apply gap-x-12;
      grid-template:
        "text image"
        ". ." theme("spacing.8")
        "form form"
        ". ." theme("spacing.3")
        "meta meta"
        / 1fr auto;
    }

    &__image {
      @apply block w-52;
    }
  }

  @container (min-width: 800px) {
    &__description {
      @apply text-lg;
    }

    &__image {
      @apply w-88;
    }
  }

  @container (min-width: 1000px) {
    &__content {
      @apply items-center;
      grid-template:
        "text image"
        ". image" theme("spacing.8")
        "form image"
        ". ." theme("spacing.4")
        "meta meta";
    }

    &__image {
      @apply h-full w-96;
    }
  }
}
</style>
